import {TextDropdown} from 'design-system/compatibility/LegacyTextDropdown';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

export type LegacyQuillEditorProps = {
  value?: string;
  variableKeys: any[];
  onChange: (content: string) => void;
};

type QuillToolbar = {
  container: HTMLElement;
};

type EditorRange = {
  index: number;
  length: number;
};

const TOOLBAR_CONFIGURATION = [
  [{header: [1, 2, 3, 4, 5, 6, false]}],
  ['bold', 'italic', 'underline', 'strike'],
  [{list: 'ordered'}, {list: 'bullet'}],
  ['link'],
  [{color: []}, {background: []}],
];

const LegacyQuillEditor = ({value, variableKeys, onChange}: LegacyQuillEditorProps) => {
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);
  const onChangeRef = useRef(onChange);
  const lastSelectionRef = useRef<EditorRange | null>(null);
  const [variablePickerHost, setVariablePickerHost] = useState<HTMLElement | null>(null);

  const variableLabels = useMemo(
    () => variableKeys.map((item: any) => `${item.title}:  [${item.key}]`),
    [variableKeys],
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const editorContainer = editorContainerRef.current;
    if (!editorContainer) {
      return;
    }

    const quill = new Quill(editorContainer, {
      theme: 'snow',
      modules: {
        toolbar: TOOLBAR_CONFIGURATION,
      },
    });
    quill.root.setAttribute('aria-label', 'Rich text editor');
    quillRef.current = quill;

    const toolbar = quill.getModule('toolbar') as QuillToolbar | undefined;
    const pickerHost = document.createElement('span');
    pickerHost.className = 'ql-formats ql-custom-button';
    toolbar?.container.appendChild(pickerHost);
    setVariablePickerHost(pickerHost);

    const handleTextChange = () => {
      onChangeRef.current(quill.root.innerHTML);
    };
    const handleSelectionChange = (range: EditorRange | null) => {
      if (range) {
        lastSelectionRef.current = range;
      }
    };
    quill.on('text-change', handleTextChange);
    quill.on('selection-change', handleSelectionChange);

    return () => {
      quill.off('text-change', handleTextChange);
      quill.off('selection-change', handleSelectionChange);
      pickerHost.remove();
      quillRef.current = null;
      lastSelectionRef.current = null;
    };
  }, []);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) {
      return;
    }
    const nextValue = value || '';
    if (quill.root.innerHTML !== nextValue) {
      quill.clipboard.dangerouslyPasteHTML(nextValue, 'silent');
    }
  }, [value]);

  const handleVariableChange = useCallback((_: unknown, selectedItem: string) => {
    const selectedIndex = variableLabels.indexOf(selectedItem);
    const selectedKey = variableKeys[selectedIndex]?.key;
    const editor = quillRef.current;
    if (!editor || selectedKey === undefined || selectedKey === null) {
      return;
    }
    const token = String(selectedKey).trim();
    const range = lastSelectionRef.current;
    const maxIndex = Math.max(0, editor.getLength() - 1);
    const insertionIndex = Math.min(Math.max(range?.index ?? maxIndex, 0), maxIndex);

    editor.focus({preventScroll: true});
    editor.insertText(insertionIndex, token, 'user');
    editor.setSelection(insertionIndex + token.length, 0, 'silent');
  }, [variableKeys, variableLabels]);

  return (
    <div
      style={{
        width: '100%',
        height: '300px',
        backgroundColor: 'white',
        flex: 1,
        paddingBottom: 40,
        color: 'black',
        marginTop: 8,
      }}>
      <div ref={editorContainerRef} />
      {variablePickerHost && createPortal(
        <div style={{marginTop: -4}}>
          <TextDropdown
            inpClassName="custom-button"
            useVirtualList
            itemHeight={40}
            popupMaxHeight="300px"
            width="30px"
            maxWidth="30px"
            popupWidth="400px"
            placeholder="  "
            dropdownColor="black"
            items={variableLabels}
            onChange={handleVariableChange}
          />
        </div>,
        variablePickerHost,
      )}
    </div>
  );
};

export default LegacyQuillEditor;
